import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { createSpinner, showSpinner, hideSpinner, setSpinner } from '@syncfusion/ej2-popups';

/**
 * app-loader — the Syncfusion spinner, identical to Labour's `app-spinner` (Bootstrap type, 45px,
 * #125AAB, own full-screen overlay). Used the same way: `<app-loader *ngIf="loading">`.
 * `[overlay]="false"` renders only the spinner, for spots that supply their own mask.
 *
 * PERF CONTRACT (this component is mounted/destroyed constantly):
 *  1. `hideSpinner()` MUST be called on destroy. The EJ2 Bootstrap spinner animates through a
 *     recursive `setTimeout(..., 18)` chain that only stops when `hideSpinner()` clears it;
 *     without it every spinner ever shown keeps ticking against a detached DOM node.
 *  2. Create/show/hide run via `NgZone.runOutsideAngular`, so those timer ticks do not trigger
 *     app-wide change detection.
 */
@Component({
  selector: 'app-loader',
  standalone: true,
  imports: [],
  templateUrl: './loader.component.html',
  styleUrl: './loader.component.css'
})
export class LoaderComponent implements AfterViewInit, OnDestroy {
  @ViewChild('spinnerContainer') spinnerContainer!: ElementRef;

  /** false = spinner only, without Labour's full-screen overlay. */
  @Input() overlay = true;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      setSpinner({ type: 'Bootstrap' });

      createSpinner({
        target: this.spinnerContainer.nativeElement,
        width: '45px'
      });

      showSpinner(this.spinnerContainer.nativeElement);
    });
  }

  ngOnDestroy(): void {
    const target = this.spinnerContainer?.nativeElement;
    if (!target) {
      return;
    }
    // Stops the recursive animation timer chain; see PERF CONTRACT above.
    this.zone.runOutsideAngular(() => hideSpinner(target));
  }
}
